/**
 * Main Application Component
 *
 * Handles routing and authentication state.
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './components/auth';
import { LoginPage, HomePage, KioskPage, OrderHistoryPage, MidnightPage } from './pages';
import { KitchenLoginPage } from './pages/KitchenLoginPage';
import { KitchenEnrollPage } from './pages/KitchenEnrollPage';
import { DeductionsPage } from './pages/DeductionsPage';
import { AdminLayout, OrdersPage, ReportsPage, MenuPage, TicketsPage, ForecastingPage, KitchenStaffPage, BirthdaysPage, PrintingPage } from './pages/admin';

// Create a client
// Failed writes must never fail silently: several admin screens await a
// mutation with no local error UI, so a save that throws (expired session,
// network drop) used to leave the typed value on screen looking saved —
// Tabari's Friday menu edit (2026-09-21) never reached the server this way.
const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Request failed';
      window.alert(
        `Save failed — the change was NOT stored.\n\n${message}\n\n` +
        `If you've been on this page a while, refresh and sign in again, then retry.`
      );
    },
  }),
});

/**
 * Protected Route Component
 * Redirects to login if not authenticated
 * Optionally allows only admins or kitchen staff
 */
function ProtectedRoute({
  children,
  adminOnly = false,
  allowKitchenStaff = false
}: {
  children: React.ReactNode;
  adminOnly?: boolean;
  allowKitchenStaff?: boolean;
}) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="relative min-h-screen">
        {/* Blurred login page in background */}
        <div className="filter blur-sm pointer-events-none">
          <LoginPage />
        </div>
        {/* Loading overlay */}
        <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="text-center bg-white/80 rounded-xl p-8 shadow-lg">
            <svg
              className="animate-spin h-10 w-10 text-blue-600 mx-auto mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="text-gray-700 font-medium">
              {sessionStorage.getItem('hmh_sso_redirect')
                ? 'Signing you in with Microsoft...'
                : 'Checking your login status...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check admin-only routes
  if (adminOnly && !user?.isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Check kitchen staff access
  if (allowKitchenStaff && !user?.isAdmin && !user?.isKitchenStaff) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/**
 * Public Route Component
 * Redirects to home if already authenticated
 */
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full shadow-lg mb-4">
              <svg
                className="w-12 h-12 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Hood Memorial Hospital
            </h1>
            <p className="text-sm text-gray-600">Lunch Ordering System</p>
          </div>
          <svg
            className="animate-spin h-10 w-10 text-blue-600 mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-gray-700 font-medium">
            {sessionStorage.getItem('hmh_sso_redirect')
              ? 'Signing you in with Microsoft...'
              : 'Checking your login status...'}
          </p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/**
 * Admin Route wrapper - includes the layout
 */
function AdminRoute({
  children,
  adminOnly = false,
  allowKitchenStaff = false
}: {
  children: React.ReactNode;
  adminOnly?: boolean;
  allowKitchenStaff?: boolean;
}) {
  return (
    <ProtectedRoute adminOnly={adminOnly} allowKitchenStaff={allowKitchenStaff}>
      <AdminLayout>
        {children}
      </AdminLayout>
    </ProtectedRoute>
  );
}

/**
 * App Routes
 */
function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Kiosk Route - No authentication required */}
      <Route path="/kiosk" element={<KioskPage />} />

      {/* Kitchen Staff Login - Public route */}
      <Route path="/kitchen" element={<KitchenLoginPage />} />
      <Route path="/kitchen/enroll" element={<KitchenEnrollPage />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/order-history"
        element={
          <ProtectedRoute>
            <OrderHistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/midnight"
        element={
          <ProtectedRoute>
            <MidnightPage />
          </ProtectedRoute>
        }
      />

      {/* Payroll deductions report - scoped permission, not full admin */}
      <Route
        path="/reports/deductions"
        element={
          <ProtectedRoute>
            <DeductionsPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route path="/admin" element={<Navigate to="/admin/orders" replace />} />
      <Route path="/admin/orders" element={<AdminRoute allowKitchenStaff><OrdersPage /></AdminRoute>} />
      <Route path="/admin/reports" element={<AdminRoute adminOnly><ReportsPage /></AdminRoute>} />
      <Route path="/admin/menu" element={<AdminRoute adminOnly><MenuPage /></AdminRoute>} />
      <Route path="/admin/tickets" element={<AdminRoute adminOnly><TicketsPage /></AdminRoute>} />
      <Route path="/admin/forecasting" element={<AdminRoute adminOnly><ForecastingPage /></AdminRoute>} />
      <Route path="/admin/birthdays" element={<AdminRoute adminOnly><BirthdaysPage /></AdminRoute>} />
      <Route path="/admin/kitchen-staff" element={<AdminRoute adminOnly><KitchenStaffPage /></AdminRoute>} />
      <Route path="/admin/printing" element={<AdminRoute adminOnly><PrintingPage /></AdminRoute>} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Main App Component
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename="/">
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
